import { firebaseErrorHintEs } from '../errors/firebaseErrorHintEs.js';
import { escapeHtml } from './htmlEscape.js';
import { isFirestoreOfflineError } from '../firestore/withFirestoreRetry.js';

/**
 * Vistas globales del shell (login, errores, carga).
 * @param {object} options
 * @param {(html: string) => void} options.render
 * @param {() => () => void} options.getLoginView
 */
export function createShellViews({ render, getLoginView }) {
  function errorView(title, err, options = {}) {
    const code = err?.code ? String(err.code) : '';
    const msg = err?.message ? String(err.message) : String(err);
    const hint = firebaseErrorHintEs(err);
    const showRetry = options.showRetry !== false && isFirestoreOfflineError(err);
    render(`
    <main class="max-w-lg mx-auto p-6">
      <h1 class="text-xl font-semibold mb-2">${escapeHtml(title)}</h1>
      <p class="text-red-700 text-sm mb-2 whitespace-pre-wrap">${escapeHtml(msg)}</p>
      ${code ? `<p class="text-xs text-slate-500 mb-2">Código: <code>${escapeHtml(code)}</code></p>` : ''}
      ${hint ? `<p class="text-slate-700 text-sm mb-4 border-l-4 border-amber-400 pl-3">${escapeHtml(hint)}</p>` : ''}
      <div class="flex flex-wrap gap-2">
        ${
          showRetry
            ? `<button type="button" id="btn-reload" class="rounded-lg bg-green-800 text-white py-2 px-4 hover:bg-green-700">Recargar página</button>`
            : ''
        }
        <button type="button" id="btn-retry" class="rounded-lg bg-slate-800 text-white py-2 px-4 hover:bg-slate-700">Ir al inicio de sesión</button>
      </div>
    </main>
  `);
    const reloadBtn = document.getElementById('btn-reload');
    if (reloadBtn) reloadBtn.onclick = () => window.location.reload();
    document.getElementById('btn-retry').onclick = () => getLoginView()();
  }

  /** Cuenta con `estadoCuenta: suspendida` (moderación). */
  function cuentaSuspendidaView(options = {}) {
    const onCerrarSesion = typeof options.onCerrarSesion === 'function' ? options.onCerrarSesion : null;
    const onValidarUbicacion =
      typeof options.onValidarUbicacion === 'function' ? options.onValidarUbicacion : null;
    const motivoUbicacion = String(options.suspensionMotivo || '') === 'ubicacion_gps_pendiente';
    const titulo = motivoUbicacion ? 'Cuenta suspendida hasta validar ubicación' : 'Cuenta suspendida';
    const texto = motivoUbicacion
      ? 'No validaste tu ubicación con GPS dentro del plazo tras las denuncias por barrio. Podés intentarlo ahora: necesitamos permiso de ubicación para comprobar que estás en La Victoria.'
      : 'Tu acceso a Mercado La Victoria fue suspendido. Si creés que es un error, contactá a la administración del barrio.';
    render(`
    <main class="min-h-screen max-w-lg mx-auto flex flex-col justify-center bg-brand-surface p-6">
      <div class="rounded-2xl border border-amber-200 bg-amber-50/80 p-5 shadow-sm">
        <h1 class="text-lg font-bold text-amber-950">${escapeHtml(titulo)}</h1>
        <p class="mt-2 text-sm leading-relaxed text-amber-950/90">${escapeHtml(texto)}</p>
        <p id="cuenta-susp-gps-msg" class="mt-3 min-h-[1.25rem] text-xs font-medium text-slate-800" role="status"></p>
        ${
          motivoUbicacion && onValidarUbicacion
            ? `<button type="button" id="btn-cuenta-suspendida-validar-gps" class="mt-2 w-full rounded-xl bg-brand-primary py-3 text-sm font-semibold text-white shadow-sm hover:brightness-95">
          Validar ubicación con GPS
        </button>`
            : ''
        }
        <button type="button" id="btn-cuenta-suspendida-signout" class="mt-3 w-full rounded-xl bg-slate-900 py-3 text-sm font-semibold text-white shadow-sm hover:brightness-95">
          Cerrar sesión
        </button>
      </div>
    </main>
  `);
    const msgEl = document.getElementById('cuenta-susp-gps-msg');
    const bVal = document.getElementById('btn-cuenta-suspendida-validar-gps');
    if (bVal && onValidarUbicacion) {
      bVal.onclick = async () => {
        bVal.disabled = true;
        if (msgEl) msgEl.textContent = 'Obteniendo ubicación…';
        try {
          await onValidarUbicacion();
          if (msgEl) msgEl.textContent = 'Listo. Recargando…';
          window.location.reload();
        } catch (err) {
          if (msgEl) {
            msgEl.textContent = String(err?.message || err || 'No se pudo validar.');
          }
          bVal.disabled = false;
        }
      };
    }
    const b = document.getElementById('btn-cuenta-suspendida-signout');
    if (b) {
      b.onclick = async () => {
        try {
          await onCerrarSesion?.();
        } catch (_e) {
          /* ignore */
        }
      };
    }
  }

  function loadingView() {
    render(`
    <main class="min-h-screen flex items-center justify-center bg-slate-50 p-6" role="status" aria-live="polite">
      <span class="sr-only">Cargando…</span>
      <div
        class="h-10 w-10 rounded-full border-2 border-slate-200 border-t-brand-primary animate-spin"
        aria-hidden="true"
      ></div>
    </main>
  `);
  }

  function missingEnvView(missing) {
    render(`
    <main class="max-w-lg mx-auto p-6">
      <h1 class="text-xl font-semibold mb-2">Configuración Firebase</h1>
      <p class="text-slate-600 mb-4">Creá un archivo <code class="bg-slate-200 px-1 rounded">.env</code> en la raíz del proyecto con las variables de tu proyecto (Console → Project settings).</p>
      <p class="text-sm text-red-700 mb-2">Faltan:</p>
      <ul class="list-disc pl-6 text-sm">${missing.map((k) => `<li><code>${escapeHtml(k)}</code></li>`).join('')}</ul>
      <p class="mt-4 text-sm text-slate-500">Podés copiar <code>.env.example</code> y completar los valores.</p>
    </main>
  `);
  }

  function renderInitFirebaseError(err) {
    render(`
    <main class="max-w-lg mx-auto p-6">
      <h1 class="text-xl font-semibold mb-2">Error al iniciar Firebase</h1>
      <p class="text-red-700 text-sm whitespace-pre-wrap">${escapeHtml(err?.message || err)}</p>
      <p class="mt-4 text-sm text-slate-600">Recargá la página (F5). Si persiste, revisá la consola del navegador (F12).</p>
    </main>
  `);
  }

  return {
    errorView,
    cuentaSuspendidaView,
    loadingView,
    missingEnvView,
    renderInitFirebaseError,
  };
}
